import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { LenticMark, DemoFooter } from '../../components/demo/DemoChrome';
import { DEMO_LIBRARY, samplePosting } from '../../data/demo';
export function Intake() {
  const {
    postingUrl,
    setPostingUrl,
    postingText,
    setPostingText,
    useSamplePosting,
    candidateCount,
    setCandidateCount,
    setMode
  } = useDemo();
  const [pasteMode, setPasteMode] = useState<'url' | 'text'>('url');
  const [collapsing, setCollapsing] = useState(false);
  const navigate = useNavigate();
  const filled = pasteMode === 'url' ? postingUrl.trim().length > 0 : postingText.trim().length > 0;
  const submit = () => {
    if (!filled || collapsing) return;
    setMode(candidateCount === 1 ? 'single' : 'cohort');
    // The field collapses upward into the source chip that opens the next screen.
    setCollapsing(true);
    window.setTimeout(() => navigate('/demo/analysis'), 220);
  };
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-625" data-mp="11ni2grfjqf2k">
      <div className="px-8 pt-8" data-id="element-626" data-mp="16nwbaboz1t1n">
        <LenticMark data-id="element-627" data-mp="16opdczczjudg" />
      </div>

      <main className="flex flex-1 justify-center px-8 py-20" data-id="element-628" data-mp="1bn8wks072s24">
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="w-full max-w-[640px]" data-id="element-629" data-mp="1bmqvni2l7pnv">
          <p className="eyebrow text-brand" data-id="element-630" data-mp="iw8r0cm7tg8c">New assessment</p>
          <h1 className="mt-4 text-[42px] font-semibold leading-[1.1] tracking-tight text-ink" data-id="element-631" data-mp="nwwi0yag1x2k">
            Start from the role.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-muted" data-id="element-632" data-mp="3reenx06nou0c">
            Paste the posting. Lentic builds the assessment from its gated scenario library — and
            shows you every check it passes on the way.
          </p>

          <motion.div initial={false} animate={{
          height: collapsing ? 0 : 'auto',
          opacity: collapsing ? 0 : 1,
          y: collapsing ? -12 : 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-12 overflow-hidden" data-id="element-633" data-mp="3obqnldrl85gz">
            <label htmlFor={pasteMode === 'url' ? 'posting-url' : 'posting-text'} className="eyebrow block text-muted" data-id="element-634" data-mp="3t5leb15m1kpk">
              Posting
            </label>
            {pasteMode === 'url' ? <input id="posting-url" value={postingUrl} onChange={e => setPostingUrl(e.target.value)} placeholder={samplePosting.url} className="mt-3 w-full border-b border-rule bg-transparent pb-3 font-mono-num text-[14px] text-ink placeholder:text-muted/50 focus:border-brand focus:outline-none" data-id="element-635" data-mp="3fcn6gfbze054" /> : <textarea id="posting-text" rows={7} value={postingText} onChange={e => setPostingText(e.target.value)} placeholder="Paste the full text of the posting." className="mt-3 w-full rounded-sm border border-rule bg-white p-4 text-[14px] leading-relaxed text-ink placeholder:text-muted/50 focus:border-brand focus:outline-none" data-id="element-636" data-mp="27jdda93km555" />}

            <div className="mt-3 flex items-center justify-between gap-4 text-[12px]" data-id="element-637" data-mp="2v9r98apa0o50">
              <button type="button" onClick={() => setPasteMode(pasteMode === 'url' ? 'text' : 'url')} className="text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:text-ink" data-id="element-638" data-mp="2vbu0ey260nbb">
                {pasteMode === 'url' ? 'paste the text instead' : 'use a URL instead'}
              </button>
              <button type="button" onClick={() => {
              setPasteMode('url');
              useSamplePosting();
            }} className="font-medium text-brand underline decoration-brand/40 underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-639" data-mp="1qc5ogx8cym1k">
                use a sample posting
              </button>
            </div>
          </motion.div>

          <div className="mt-14" data-id="element-640" data-mp="2925blekn1r2c">
            <p className="eyebrow text-muted" data-id="element-641" data-mp="2511vcqyoht0l">Candidates</p>
            <div className="mt-3 flex items-center gap-5" data-id="element-642" data-mp="251leuc3z8pgq">
              <button type="button" aria-label="Fewer candidates" onClick={() => setCandidateCount(Math.max(1, candidateCount - 1))} className="flex h-9 w-9 items-center justify-center rounded-sm border border-rule text-muted transition-colors hover:border-ink hover:text-ink" data-id="element-643" data-mp="209tjzpdsfxks">
                <MinusIcon className="h-4 w-4" data-id="element-644" data-mp="2nxungjw40ntc" />
              </button>
              <span className="font-mono-num text-[40px] font-medium leading-none text-ink" data-id="element-645" data-mp="2vcdo43b7nm3l">
                {candidateCount}
              </span>
              <button type="button" aria-label="More candidates" onClick={() => setCandidateCount(Math.min(12, candidateCount + 1))} className="flex h-9 w-9 items-center justify-center rounded-sm border border-rule text-muted transition-colors hover:border-ink hover:text-ink" data-id="element-646" data-mp="3599nnjj4sxw0">
                <PlusIcon className="h-4 w-4" data-id="element-647" data-mp="3j7geifpnxxm6" />
              </button>
            </div>
            <div className="relative mt-4 min-h-[42px] max-w-md" data-id="element-648" data-mp="3o0strz8gcs29">
              <AnimatePresence mode="wait" initial={false} data-id="element-649" data-mp="3sykt3ebffggh">
                <motion.p key={candidateCount === 1 ? 'single' : 'cohort'} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="text-[13.5px] leading-relaxed text-muted" data-id="element-650" data-mp="3sy1dw5d5v665">
                  {candidateCount === 1 ? 'One candidate — a tailored deep probe. Results won’t be comparable to anyone else’s, by design.' : `${candidateCount} candidates — one identical instrument for all. That’s what makes the results comparable, and the comparison defensible.`}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>

          <div className="mt-14 flex justify-end" data-id="element-651" data-mp="2glnsornqdo0d">
            <button type="button" onClick={submit} disabled={!filled} className="group inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-muted/25 disabled:text-muted" data-id="element-652" data-mp="2umgn7ce18j3h">
              Analyse the role
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-653" data-mp="36x7n0zqxqzz9" />
            </button>
          </div>

          <p className="eyebrow mt-14 normal-case tracking-[0.06em] text-muted/70" data-id="element-654" data-mp="2y3un3dugey3o">
            FIN library · {DEMO_LIBRARY} · last gated against 3 frontier models
          </p>
        </motion.div>
      </main>

      <DemoFooter data-id="element-655" data-mp="2o55cm64w0fxg" />
    </div>;
}